import type { LeadTimelineEvent } from '@/api/types';
import { formatDateTime } from './format';

/**
 * Marker tone per event kind. Only three states are coloured — an ordinary step is neutral, and
 * reserving the status palette for the two events that are actually good or bad news is what keeps
 * those two readable.
 */
const KIND_TONE: Record<string, 'neutral' | 'good' | 'bad'> = {
  captured: 'neutral',
  chat_started: 'neutral',
  first_reply: 'good',
  missed: 'bad',
  chat_closed: 'neutral',
  rated: 'good',
  ai_scored: 'neutral',
  status: 'neutral',
};

/**
 * What happened to this lead, in order.
 *
 * Absolute times rather than "3 hours later": the gaps between events are the story (a first reply
 * at 09:04 on a chat opened at 08:12 is the finding), and relative labels hide exactly that.
 *
 * Events with no timestamp are not rendered as placeholders — see the server's Timeline builder.
 */
export function LeadTimeline({ events }: { events: LeadTimelineEvent[] }) {
  if (events.length === 0) {
    return <div className="empty-state t-body">Nothing recorded for this lead yet.</div>;
  }

  return (
    <ol className="lead-timeline">
      {events.map((event, index) => (
        <li key={`${event.kind}-${event.at}-${index}`} className="timeline-item">
          <span className={`timeline-dot timeline-dot-${KIND_TONE[event.kind] ?? 'neutral'}`} aria-hidden="true" />
          <div className="timeline-body">
            <div className="timeline-head">
              <span className="t-label">{event.title}</span>
              <time className="t-caption subtle tabular" dateTime={event.at}>{formatDateTime(event.at)}</time>
            </div>
            {event.detail && <div className="t-caption subtle">{event.detail}</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}
